import * as _ from 'lodash'
import {BehaviorSubject, throwError} from 'rxjs'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IwbMetric, IwbPlan, IwbPlanOverride} from '../PA.Core/PAdbSchemaRowsIF';
import {
    IHaveSourceCustomers, IHaveProductGroups, IHaveExecutionDates, IHaveFinishedProducts,
    IHaveExecutionBounds, IHaveSecurity
} from '../PA.Core/PA.Core.IF.Aspects';
import {
    IIdentityProvider, IProgressProvider, IMessageProvider, IDateRange,
    MessageType, IMessage
} from '../PA.Core/PA.Core.IF.Basic';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {ASync} from '../PA.Core/Basic/ASync'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'

import {PAdbContext} from '../PA.Core/PAdbContext';
import {
    esWB_Plan,
    esWB_Plan_Override,
    esWB_Plan_Product_Group,
    esWB_Plan_Product,
    esWB_Customer,
    esWB_Metric,
    esWB_Plan_Volume_Metrics,
    esWB_Plan_Redistrib_Volume_Metrics,
    esWB_Plan_Volume_Monthly_Metrics, esWB_Plan_Funding_Metrics
} from "../PA.Core/PAdbSchema";

import {
    WB_PlanRow, WB_CustomerRow, WB_PlanOverrideRow, WB_ProductGroupRow,
    WB_PlanProductRow, WB_FinishedProductRow, WB_PlanProductGroupRow, WB_MetricRow
} from '../PA.Core/PAdbSchemaRows';

import {SourceCustomerService} from '../PA.MD.Services/SourceCustomerService'
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {IHaveEvents, IHaveEventsListenerFunction} from "../PA.TPM.Core/PA.TPM.IF.Aspects";
import {EventCollection} from "../PA.TPM.Core/EventCollection";
import {PAusrContext} from "../PA.Core/PAusrContext";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {EventBO, IEventSaveValidateOptions} from "./EventBO";
import {BusinessObjectValidator} from "../PA.Core/BusinessObject/BusinessObjectValidator";
import {Subscription} from "rxjs/Subscription";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {IAutoAnchorManager, IHaveAutoAnchorManager} from "../PA.TPM.Services/PA.TPM.Services.IF";
import {AutoAnchorManager} from "../PA.TPM.Services/AutoAnchorManager";
import {IPolicyCollection} from "../PA.Core/PA.Core.IF.Policy";
import {PolicyCollection} from "../PA.Core/Security/PolicyCollection";
import {PolicyBOis} from "../PA.Core/Security/PolicyBOis";
import {PolicyHasRole} from "../PA.Core/Security/PolicyHasRole";
import {PALockClient} from "../PA.Concurrency.Services/PALockClient";
import {IHaveEditorLock, ILock, ILockOptions} from "../PA.Concurrency.Core/PA.Concurrency.IF.Lock";
import {IHaveRefCounter, IRefCounter} from "../PA.Concurrency.Core/PA.Concurrency.IF.RefCounter";
import {RefCounter} from "../PA.Concurrency.Core/RefCounter";
import {MDUsageService} from "../PA.MD.Services/MDUsageService";

//<editor-fold desc = "Plan BO Options Interface and Defaults ">

export interface IPlanCreateOptions {
    bEagerLoadAspectInterfaces?: boolean
    dbContext?: PAdbContext,
    EventCollection?: EventCollection,
    bEventCollectionLoadAllEventThreshold?: number
}

const PlanCreateOptions_default: IPlanCreateOptions = {
    bEagerLoadAspectInterfaces: false,
    bEventCollectionLoadAllEventThreshold: 1
}

export interface IPlanLoadOptions {
    dbContext?: PAdbContext
}

const PlanLoadOptions_default: IPlanLoadOptions = {}

export interface IPlanSaveValidateOptions {
    Validate?: {
        bSkipAllValidations?: boolean
        bSkipAnchorOverlapValidation?: boolean,
        bSkipEventCollectionValidation?: boolean
    },
    Save?: {},
    bIncludeEventCollection?: boolean,
    EventSaveValidateOptions?: IEventSaveValidateOptions
}

const PlanSaveValidateOptions_default: IPlanSaveValidateOptions = {
    Validate: {
        bSkipAllValidations: false,
        bSkipAnchorOverlapValidation: false,
        bSkipEventCollectionValidation: false
    },
    Save: {},
    bIncludeEventCollection: true,
    EventSaveValidateOptions: {}
}

export interface IPlanDeleteOptions {
}

const PlanDeleteOptions_default: IPlanDeleteOptions = {}

//</editor-fold desc>

export class PlanBO
    implements IBusinessObject,
        IHaveSourceCustomers,
        IHaveProductGroups,
        IHaveFinishedProducts,
        IHaveExecutionDates,
        IHaveExecutionBounds,
        IHaveAutoAnchorManager,
        IHaveSecurity,
        IHaveEditorLock,
        IHaveRefCounter
    // IRecordResolverProvider  <static>
{
    //<editor-fold desc = "Plan Attributes and Constructor">
    CreateOptions: IPlanCreateOptions
    Identity: IdentityProvider;

    LoadOptions: IPlanLoadOptions;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    _bIsLoadedInternal: boolean;
    _OnLoadCompleteInternal: Promise<PlanBO>
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Plan: WB_PlanRow;
    private ecPlanEvents: EventCollection

    usrContext: PAusrContext

    constructor(options?: IPlanCreateOptions) {

        this.CreateOptions = options || {}
        _.defaults(this.CreateOptions, PlanCreateOptions_default)

        if ('EventCollection' in this.CreateOptions) {
            this.ecPlanEvents = this.CreateOptions.EventCollection
            this.dbContext = this.ecPlanEvents.dbContext
        }
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext
        }

        this.usrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
        this.Identity = new IdentityProvider({id: 0, name: 'Undefined Plan'})

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._bIsLoadedInternal = false

        this._OnLoadComplete = undefined
        this._OnLoadCompleteInternal = undefined
        this.LoadProgressProvider = new ProgressProvider('Loading', this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_Plan = undefined

        if (_.isEmpty(this.ecPlanEvents)) {
            this.ecPlanEvents = new EventCollection()
        }

        this.IHaveSourceCustomers_init()
        this.IHaveProductGroups_init()
        this.IHaveFinishedProducts_init()
        this.IHaveExecutionDates_init()
        this.IHaveExecutionBounds_init()
        this.IHaveSecurity_init()
        this.RefCounter_Init()
    }

    //</editor-fold>

    get WB_Plan(): WB_PlanRow {
        return this.drWB_Plan;
    }

    get PlanEvents(): IHaveEvents {
        return this.ecPlanEvents
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;

        //precheck promises.. to see if already loading..?

        self.LoadProgressProvider.start("Creating Plan");

        self._OnLoadCompleteInternal = new Promise<PlanBO>((resolve, reject) => {
            if (self.bIsLoaded) {
                // "Plan already loaded.";
                resolve(self);
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, PlanLoadOptions_default)

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                reject("Unable to determine dbContext for new Plan.");
                return;
            }

            self.ecPlanEvents.dbContext = self.dbContext
            self.ecPlanEvents.addPlanBO(this)
            self.ecPlanEvents.loadAllProductGroups()

            reject("PlanBo creation is Not Implemented")
        });

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self._OnLoadCompleteInternal
                .then((aBO) => {
                    if (self.bIsLoaded) {
                        resolve(self)
                        return;
                    }

                    // Run after Load
                    self._bIsLoadedInternal = true
                    self._OnLoadCompleteInternal = undefined
                    self._AfterBoLoadCompleteInternal()
                        .then(() => {
                            self._bIsLoaded = true;
                            self._OnLoadComplete = undefined
                            self.LoadProgressProvider.finish()
                            if (self.CreateOptions.bEagerLoadAspectInterfaces) {
                                self.OnIHaveSourceCustomersLoaded
                                self.OnIHaveProductGroupsLoaded
                                self.OnIHaveExecutionDatesLoaded
                            }
                            resolve(self)
                        })
                        .catch((err) => {
                            self._OnLoadCompleteInternal = undefined;
                            self._OnLoadComplete = undefined;
                            self.LoadProgressProvider.exception(err);
                            resolve(err)
                        })
                })
                .catch((err) => {
                    self._OnLoadCompleteInternal = undefined;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    resolve(self)
                })
        })

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadCompleteInternal = new Promise<PlanBO>((resolve, error) => {

            if (self.bIsLoaded) {
                resolve(self); // "Plan already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, PlanLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for new Plan.")
                return;
            }

            self.ecPlanEvents.dbContext = self.dbContext

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = PlanBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = PlanBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let aPlanRow: any = recordResolver.get(esWB_Plan, anId)

                    if (_.isEmpty(aPlanRow)) {
                        console.log("Plan Record Resolver unable to locate Plan for id: ", anId)
                        error("Plan not Found");
                        return;
                    }

                    self.drWB_Plan = aPlanRow;
                    self.Identity.setAll(self.WB_Plan.cif.WB_PLAN_ID, self.WB_Plan.cif.PLAN_ID, self.WB_Plan.cif.PLAN_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Plan, 'WB_PLAN_ID', 'PLAN_ID', 'PLAN_NAME')

                    resolve(self)
                })
                .catch(function (err: any) {
                    error(err)
                    return;
                });
        });

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self._OnLoadCompleteInternal
                .then((aBO) => {
                    if (self.bIsLoaded) {
                        resolve(self)
                        return;
                    }

                    // Run after Load
                    self._bIsLoadedInternal = true
                    self._OnLoadCompleteInternal = undefined
                    self._AfterBoLoadCompleteInternal()
                        .then(() => {
                            self._bIsLoaded = true;
                            self._OnLoadComplete = undefined
                            self.LoadProgressProvider.finish()

                            self.ecPlanEvents.addPlanBO(self)

                            if (self.CreateOptions.bEagerLoadAspectInterfaces) {
                                self.OnIHaveSourceCustomersLoaded
                                self.OnIHaveProductGroupsLoaded
                                self.OnIHaveExecutionDatesLoaded
                            }

                            // Check if the threshold is exceeded
                            self.ecPlanEvents.getEventIds(false)
                                .then((evtIds: number[]) => {
                                    if (self.CreateOptions.bEventCollectionLoadAllEventThreshold >= evtIds.length) {
                                        self.ecPlanEvents.loadAllProductGroups()
                                    }
                                })

                            resolve(self)
                        })
                        .catch((err) => {
                            self._OnLoadCompleteInternal = undefined;
                            self._OnLoadComplete = undefined;
                            self.LoadProgressProvider.exception(err);
                            resolve(self)
                        })
                })
                .catch((err) => {
                    self._OnLoadCompleteInternal = undefined;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    resolve(self)
                })
        })

        return this._OnLoadComplete;
    }

    _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this;
        return new Promise<void>((resolve, error) => {
            let tasks: Promise<any>[] = [];
            self.LoadProgressProvider.setProgressMessage('PlanBO.PostLoad', 'Processing Loaded Data');
            tasks.push(self._Overrides_OnBoLoadComplete())
            tasks.push(self._AutoAnchor_OnBoLoadComplete())
            Promise.all(tasks)
                .then((results: any) => {
                    return self.IHaveSecurity_OnLoadComplete()
                })
                .then(() => {
                    if (!this.RefCounter.RefCount)
                        self.RefCounter.acquireReference()

                    if (_.isNumber(self.drWB_Plan.cif.WB_SALESORG_ID) && self.drWB_Plan.cif.WB_SALESORG_ID > 0) {
                        let mruService = new MDUsageService()
                        return mruService.saveSalesOrgUsage(self.drWB_Plan.cif.WB_SALESORG_ID)
                    } else {
                        resolve();
                        return;
                    }
                })
                .then(() => {
                    resolve();
                })
                .catch((err: any) => {
                    error(err);
                });
        })
    }

    async _Overrides_OnBoLoadComplete(): Promise<void> {
        const self = this;
        try {
            let drOverrides: WB_PlanOverrideRow;

            if (_.isEmpty(self.drWB_Plan.WB_PlanOverrideRow)) {
                drOverrides = <any>(self.dbContext.createRecord(esWB_Plan_Override));
                drOverrides.setDefaults()
                var newRow: IwbPlanOverride = drOverrides;
                newRow.WB_PLAN_ID = self.WB_Plan.cif.WB_PLAN_ID;
                self.drWB_Plan.WB_PlanOverrideRow = drOverrides;
                self.dbContext.add(esWB_Plan_Override, drOverrides)
                await self.dbContext.Save(esWB_Plan_Override, drOverrides)
            } else
                drOverrides = <WB_PlanOverrideRow>self.drWB_Plan.WB_PlanOverrideRow;

            // Create Metrics row if empty
            if (_.isEmpty(drOverrides.WB_MetricRow_Actual)) {
                self.dbContext.createRecordWithKey(esWB_Metric).then(async (drMetrics: WB_MetricRow) => {
                    drMetrics.setDefaults()
                    var Metrics: IwbMetric = drMetrics;
                    Metrics.WB_PLAN_ID = self.WB_Plan.cif.WB_PLAN_ID
                    Metrics.REFERENCE_LEVEL_ID = 'Plan'
                    Metrics.METRIC_TYPE_ID = 'Actual'
                    Metrics.REFERENCE_ID = self.WB_Plan.cif.WB_PLAN_ID
                    Metrics.REFERENCE_ID_UI = self.WB_Plan.cif.PLAN_ID;
                    Metrics.WB_PRDGRP_ID = 0;
                    drOverrides.WB_MetricRow_Actual = drMetrics;
                    self.dbContext.add(esWB_Metric, drMetrics);
                    await self.dbContext.Save(esWB_Metric, drMetrics)
                })
            }
            return
        } catch (err) {
            throw(err)
            return
        }


    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options: IPlanSaveValidateOptions): Promise<IBusinessObject> {

        const self = this;

        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_Plan.cif.PLAN_NAME + "(" + self.WB_Plan.cif.PLAN_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {

            options = options || {}
            _.defaultsDeep(options, PlanSaveValidateOptions_default)

            self.ValidateOptions = options;

            if (_.isEmpty(self.dbContext)) {
                var sError = "Unable to determine dbContext to Validate Plan.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
                return
            }

            var eventValidator: BusinessObjectValidator = undefined
            var subProgressChanged: Subscription = undefined

            ASync.resolve()
                .then((ignore) => {

                    // Do Plan Level Validations..


                    return ASync.resolve(self)

                }).then((ignore) => {

                if (options.bIncludeEventCollection &&
                    (!options.Validate.bSkipEventCollectionValidation) &&
                    (!options.Validate.bSkipAllValidations)) {

                    // Create Validator... and Validate..
                    var eventsToValidate = self.PlanEvents.Events
                    if (eventsToValidate.length == 0) {
                        return ASync.resolve(self)
                    }
                    var initialProgress = self.ValidateProgressProvider.process_pcnt_complete
                    var remainder = Math.max(0, 1.0 - initialProgress)

                    eventValidator = new BusinessObjectValidator(eventsToValidate, options.EventSaveValidateOptions)
                    subProgressChanged = eventValidator.ValidateProgressProvider.ProgressChanged.subscribe(function (aProgressProvider: IProgressProvider) {
                        if (!_.isEmpty(aProgressProvider))
                            self.ValidateProgressProvider.setPercentComplete(initialProgress + (aProgressProvider.process_pcnt_complete * remainder))
                    })

                    return eventValidator.Validate()

                } else {
                    return ASync.resolve(self)
                }

            }).then((ignore) => {

                if (subProgressChanged) {
                    subProgressChanged.unsubscribe()
                }

                if (eventValidator) {

                    _.forEach(eventValidator.ValidatedBOs, (anEventBO: EventBO) => {
                        _.forEach(anEventBO.ValidateMessageProvider.Messages, (aMessage: IMessage) => {
                            if (_.isEmpty(aMessage.subtitle)) {
                                aMessage.subtitle = anEventBO.WB_Event.cif.EVENT_ID
                            } else {
                                aMessage.subtitle = aMessage.subtitle + ': ' + anEventBO.WB_Event.cif.EVENT_ID
                            }
                            self.ValidateMessageProvider.addMessage(aMessage)
                        })
                    })

                }

                return ASync.resolve()

            }).then((ignore) => {
                self._bIsValidated = true;
                self.ValidateProgressProvider.finish();
                resolve(self);
            }).catch((err) => {

                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);
                resolve(self)
            })

        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: IPlanSaveValidateOptions): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: IPlanSaveValidateOptions): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: IPlanSaveValidateOptions): Promise<IBusinessObject> {

        const self = this;
        this.SaveProgressProvider.start("Saving " + this.WB_Plan.cif.PLAN_NAME + "(" + this.WB_Plan.cif.PLAN_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {
            if (!PAEnvironment.isNode()) {
                if (self.EditorLock.Status === "ReadOnly") {
                    self._bIsSaved = false;
                    var sError = "Unable to Save Locked Plan.";
                    self.SaveProgressProvider.exception(sError);
                    reject(sError);
                    return
                }
            }

            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Plan.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            options = options || {}
            _.defaultsDeep(options, PlanSaveValidateOptions_default)
            self.SaveOptions;

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            let bDone = false

            ASync.resolve()
                .then((ignored) => {
                    self.SaveProgressProvider.setPercentComplete(0.1)
                    if (options.Validate.bSkipAllValidations) {
                        return ASync.resolve()
                    } else {
                        return self.Validate(options)
                        // Run Validate
                    }
                })
                .then((ignore) => {
                    if (bDone) {
                        return
                    }

                    let tasks: Promise<any>[] = []

                    tasks.push(saveFunction(esWB_Plan, self.drWB_Plan))
                    tasks.push(saveFunction(esWB_Plan_Override, self.drWB_Plan.WB_PlanOverrideRow))

                    if (self.drWB_Plan.WB_PlanOverrideRow) {
                        tasks.push(saveFunction(esWB_Metric, self.drWB_Plan.WB_PlanOverrideRow.WB_MetricRow_Actual))
                    }
                    return Promise.all(tasks)
                })
                .then((ignored) => {
                    if (bDone) {
                        return
                    }

                    // Validate all event, placeholder
                    return ASync.resolve();

                })
                .then((ignored) => {
                    if (bDone) {
                        return
                    }

                    // Use Batch Save for Event Save if not already
                    //Saving Events
                    let arrEvents = self.PlanEvents.Events

                    if (!bBatch) {
                        self.dbContext.BatchStart()
                    }

                    async function saveEvent() {
                        for (let i = 0; i < arrEvents.length; i++) {
                            self.SaveProgressProvider.setPercentComplete(0.1 + (0.8 * (i / arrEvents.length)))
                            let event = arrEvents[i]
                            await event.SaveBatch({Save: {bIncludeEventCollection: false}})
                        }

                        if (!bBatch) {
                            await self.dbContext.BatchComplete()
                        }
                    }

                    return saveEvent()

                })
                .then((ignored) => {
                    if (bDone) {
                        return
                    }

                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    if (bDone) {
                        return
                    }
                    self.SaveProgressProvider.exception(err);
                    resolve(self)
                })
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(): Promise<IBusinessObject> {
        const self = this;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + self.WB_Plan.cif.PLAN_NAME + "(" + self.WB_Plan.cif.PLAN_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {

            // 1. Validate if we can delete the plan
            if (self != null) { // true
                let errorText = "Deleting plans is not supported.";
                self.DeleteMessageProvider.addMessage({
                    type: MessageType.Error,
                    title: "Delete Error", description: errorText, code: "Unimplemented"
                });
                self.DeleteProgressProvider.exception(errorText);
                resolve(self);
                return;
            }
            ;

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
            resolve(self);
        });

    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveSourceCustomers operations">

    SourceCustomersChanged: BehaviorSubject<IHaveSourceCustomers>;

    _bAllSourceCustomersLoaded: boolean;
    _AllSourceCustomers: WB_CustomerRow[];
    _OnIHaveSourceCustomersLoaded: Promise<IHaveSourceCustomers>;

    IHaveSourceCustomers_init(): void {
        this.SourceCustomersChanged = new BehaviorSubject<IHaveSourceCustomers>(this);
        this._AllSourceCustomers = [];
        this._bAllSourceCustomersLoaded = false;
        this._OnIHaveSourceCustomersLoaded = undefined;
    }


    get SourceCustomersReadOnly(): boolean {
        return true;
    }


    addSourceCustomer(SourceCustomerId: number): Promise<void> {
        return ASync.error("addSourceCustomer not implemented for Plans.. SubCustomers are ReadOnly");
    }

    removeSourceCustomer(SourceCustomerId: number): Promise<void> {
        return ASync.error("removeSourceCustomer not implemented for Plans.. SubCustomers are ReadOnly");
    }

    hasSourceCustomer(SourceCustomerId: number): boolean {
        return true; // Plans have all source Customers
    }

    isSourceCustomerEditable(SourceCustomerId: number): boolean {
        return false;
    }


    get bIHaveSourceCustomersLoaded(): boolean {
        return this._bAllSourceCustomersLoaded;
    }


    get SourceCustomers(): WB_CustomerRow[] {
        return this._AllSourceCustomers; // for plans.. it contains all source customers.. can not be changed..
    }

    get AllSourceCustomers(): WB_CustomerRow[] {
        return this._AllSourceCustomers;
    }

    get OnIHaveSourceCustomersLoaded(): Promise<IHaveSourceCustomers> {
        const self = this;

        if (self._bAllSourceCustomersLoaded)
            return new Promise<IHaveSourceCustomers>((resolve, err) => {
                resolve(self)
            });

        if (!_.isEmpty(self._OnIHaveSourceCustomersLoaded))
            return self._OnIHaveSourceCustomersLoaded; // Load already started, wait on same Promise

        self._OnIHaveSourceCustomersLoaded = new Promise<IHaveSourceCustomers>((resolve, error) => {
            // If the BO is not loaded, throw an error.. It should have been started
            if ((!(self.bIsLoaded)) && (_.isEmpty(self.OnLoadComplete))) {
                error("Plan isn't Loaded or Loading.. Can't determine Source Customers");
                return;
            }

            if (!(self.bIsLoaded)) { // wait on LoadPromise
                self.OnLoadComplete.then((aPlanBO) => {
                    if (aPlanBO.bIsLoaded)
                        return self.OnAllSourceCustomersLoaded_WhenPlanIsLoaded.then((src_cust_rows) => {
                            resolve(self);
                        }).catch((err) => {
                            error(err)
                        });
                    else
                        error("Waited on Plan Load to complete but still isn't Loaded");
                })
            }

            return self.OnAllSourceCustomersLoaded_WhenPlanIsLoaded.then((src_cust_rows) => {
                resolve(self);
            }).catch((err) => {
                error(err)
            });

        });
        return self._OnIHaveSourceCustomersLoaded;
    }

    get OnAllSourceCustomersLoaded_WhenPlanIsLoaded(): Promise<WB_CustomerRow[]> {
        const self = this;
        return new Promise<WB_CustomerRow[]>((resolve, error) => {
            // Plan Is Loaded..
            // Determine the Planning Customer/ Sub Planning Customer
            let iPlanningCustomer = _.toNumber(self.WB_Plan.cif.WB_CUST_ID_PLANNING);
            let iSubPlanningCustomer = _.toNumber(self.WB_Plan.cif.WB_CUST_ID_SUBPLANNING);

            let iParentCustomers: number[] = []
            if (_.isNumber(iPlanningCustomer) && (iPlanningCustomer != 0))
                iParentCustomers.push(iPlanningCustomer)
            if (_.isNumber(iSubPlanningCustomer) && (iSubPlanningCustomer != 0))
                iParentCustomers.push(iSubPlanningCustomer)

            let iSourceCustomersIds: number[] = []

            let aSourceCustomerService = new SourceCustomerService();
            aSourceCustomerService.getSrcCustIdsbyParent(iParentCustomers)
                .then((arrSourceCustomers) => {
                    iSourceCustomersIds = arrSourceCustomers

                    if (iSourceCustomersIds.length > 0) {
                        return self.dbContext.findAll(esWB_Customer, {where: {WB_CUST_ID: {'inq': iSourceCustomersIds}}})
                    } else {
                        return new Promise<any>((resolve, error) => {
                            resolve([])
                        })
                    }

                })
                .then((src_cust_rows) => {
                    self._AllSourceCustomers = src_cust_rows;
                    self._bAllSourceCustomersLoaded = true;
                    self._OnIHaveSourceCustomersLoaded = undefined;
                    resolve(src_cust_rows);
                })
                .catch((err) => {
                    error(err);// crap happened
                })
        });
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveProductGroup operations">

    _bIHaveProductGroupsLoaded: boolean;
    _OnIHaveProductGroupsLoaded: Promise<IHaveProductGroups>;


    IHaveProductGroups_init() {
        this.ProductGroupsChanged = new BehaviorSubject<IHaveProductGroups>(this);
        this._bIHaveProductGroupsLoaded = false;
        this._OnIHaveProductGroupsLoaded = undefined;
    }


    ProductGroupsChanged: BehaviorSubject<IHaveProductGroups>;

    get bIHaveProductGroupsLoaded(): boolean {
        return this._bIHaveProductGroupsLoaded
    }

    get OnIHaveProductGroupsLoaded(): Promise<IHaveProductGroups> {

        const self = this;

        if (self._OnIHaveProductGroupsLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHaveProductGroupsLoaded))
            return self._OnIHaveProductGroupsLoaded; // Load already started, wait on same Promise

        self._OnIHaveProductGroupsLoaded = new Promise<IHaveProductGroups>((resolve, error) => {
            // If the BO is not loaded, throw an error.. It should have been started
            if ((!(self.bIsLoaded)) && (_.isEmpty(self.OnLoadComplete))) {
                error("Plan isn't Loaded or Loading.. Can't determine Source Customers");
                return;
            }

            if (!(self.bIsLoaded)) { // wait on LoadPromise
                self.OnLoadComplete.then((aPlanBO) => {
                    if (aPlanBO.bIsLoaded) {
                        // Setup Product Group Collection, flag loaded.
                        self.OnIHaveProductGroupsLoaded_WhenPlanIsLoaded()
                        resolve(self)
                    } else {
                        error("Waited on Plan Load to complete but still isn't Loaded");
                    }
                })
            }

            // Setup Product Group Collection, flag loaded.
            self.OnIHaveProductGroupsLoaded_WhenPlanIsLoaded()
            resolve(self);

        });

        return self._OnIHaveProductGroupsLoaded;
    }

    private OnIHaveProductGroupsLoaded_WhenPlanIsLoaded(): void {

        this._bIHaveProductGroupsLoaded = true;
        this._OnIHaveProductGroupsLoaded = undefined
    }

    get ProductGroups(): WB_ProductGroupRow[] {
        return this.AllProductGroups
    }

    get AllProductGroups(): WB_ProductGroupRow[] {
        return <WB_ProductGroupRow[]>(_.map((<any>this.drWB_Plan).PlanProductGroups, 'ProductGroup'))
    }

    get ProductGroupsReadOnly(): boolean {
        return true;
    }

    addProductGroup(ProductGroupId: number): Promise<void> {
        return ASync.error("Product Groups can't be added to Plans");
    }

    removeProductGroup(ProductGroupId: number): Promise<void> {
        return ASync.error("Product Groups can't be removed from Plans");
    }

    hasProductGroup(ProductGroupId: number): boolean {
        return true
    }

    isProductGroupEditable(ProductGroupId: number): boolean {
        return false;
    }


//</editor-fold>

    //<editor-fold desc = "IHaveExecutionDates operations">

    IHaveExecutionDates_init(): void {
        this._bIHaveExecutionDatesLoaded = false
        this._OnIHaveExecutionDatesLoaded = undefined
        this.DatesChanged = new BehaviorSubject<IHaveExecutionDates>(this)
    }

    _bIHaveExecutionDatesLoaded: boolean
    _OnIHaveExecutionDatesLoaded: Promise<IHaveExecutionDates>

    get bIHaveExecutionDatesLoaded(): boolean {
        return this._bIHaveExecutionDatesLoaded
    }

    get OnIHaveExecutionDatesLoaded(): Promise<IHaveExecutionDates> {
        const self = this;
        // triggers load progress, if not loaded

        // if loaded, resolve with self
        if (this._bIHaveExecutionDatesLoaded) {
            return ASync.resolve(self);
        }

        // if load in progress, wait on same load
        if (!_.isEmpty(this._OnIHaveExecutionDatesLoaded)) {
            return this._OnIHaveExecutionDatesLoaded
        }

        self._OnIHaveExecutionDatesLoaded = new Promise<IHaveExecutionDates>((resolve, reject) => {
            // check if BO is loaded
            if (self.bIsLoaded) {
                resolve(self)
                return;
            }

            if (_.isEmpty(self.OnLoadComplete)) {
                reject("Plan failed to Load")
                return;
            }

            self.OnLoadComplete
                .then((aPlanBO) => {

                    self._bIHaveExecutionDatesLoaded = true
                    self._OnIHaveExecutionDatesLoaded = undefined // should we do this, or just hold onto it ?
                    resolve(self)
                })
                .catch((err) => {
                    reject(err)
                })

        })

        return self._OnIHaveExecutionDatesLoaded
    }

    // InStore
    get InStore(): IDateRange {
        return this.drWB_Plan.InStore.Range
    }

    set InStore(value: IDateRange) {
        // Plan Dates can't be changed../ Overridden
        //this.drWB_Plan.InStore.Range = value
    }

    get InStoreSource(): IDateRange {
        return this.InStore
    }

    get InStoreBounds(): IDateRange {
        return this.drWB_Plan.InStore.Range
    }

    get bInStoreDatesExist(): boolean {
        return true;
    }

    get bInStoreDatesValid(): boolean {
        return true; // Can't be changed
    }

    get bInStoreDatesReadOnly(): boolean {
        return true
    }

    // Shipment
    get Shipment(): IDateRange {
        return this.drWB_Plan.Shipment.Range
    }

    set Shipment(value: IDateRange) {
        // Plan Dates can't be changed.. or Overridden
        //this.drWB_Plan.InStore.Range = value
    }

    get ShipmentSource(): IDateRange {
        return this.Shipment
    }

    get ShipmentBounds(): IDateRange {
        return this.drWB_Plan.Shipment.Range
    }

    get bShipmentDatesExist(): boolean {
        return true;
    }

    get bShipmentDatesValid(): boolean {
        return true;
    }

    get bShipmentDatesReadOnly(): boolean {
        return true
    }

    get Order(): IDateRange {
        return {StartDt: undefined, EndDt: undefined}
    }

    set Order(value: IDateRange) {
        return;
    }

    get OrderSource(): IDateRange {
        return this.Order
    }

    get OrderBounds(): IDateRange {
        return {StartDt: undefined, EndDt: undefined}
    }

    get bOrderDatesExist(): boolean {
        return false
    }

    get bOrderDatesValid(): boolean {
        return true;
    }

    get bOrderDatesReadOnly(): boolean {
        return true
    }

    DatesChanged: BehaviorSubject<IHaveExecutionDates>;


//</editor-fold>

    //<editor-fold desc = "IHaveFinishedProducts operations">

    IHaveFinishedProducts_init(): void {

        this._bIHaveFinishedProductsLoaded = false
        this._OnIHaveFinishedProductsLoaded = undefined
        this.FinishedProductsChanged = new BehaviorSubject<IHaveFinishedProducts>(this)

    }

    _bIHaveFinishedProductsLoaded: boolean;
    _OnIHaveFinishedProductsLoaded: Promise<IHaveFinishedProducts>;

    FinishedProductsChanged: BehaviorSubject<IHaveFinishedProducts>;

    get bIHaveFinishedProductsLoaded(): boolean {
        return this._bIHaveFinishedProductsLoaded
    }

    get FinishedProductsReadOnly(): boolean {
        return true
    }

    get FinishedProducts(): WB_FinishedProductRow[] {
        var returnValue: WB_FinishedProductRow[] = []
        _.forEach(this.drWB_Plan.WB_Plan_ProductGroupRows,
            (aPlnPG: WB_PlanProductGroupRow) => {
                _.forEach(aPlnPG.WB_PlanProductRows,
                    (aPlnPrd: WB_PlanProductRow) => {
                        if (aPlnPrd.WB_FinishedProductRow) {
                            returnValue.push(aPlnPrd.WB_FinishedProductRow)
                        }
                    })
            })
        return returnValue
    }

    get AllFinishedProducts(): WB_FinishedProductRow[] {
        return this.FinishedProducts
    }

    get OnIHaveFinishedProductsLoaded(): Promise<IHaveFinishedProducts> {
        const self = this;
        // triggers load progress, if not loaded

        // if loaded, resolve with self
        if (this._bIHaveFinishedProductsLoaded) {
            return ASync.resolve(self);
        }

        // if load in progress, wait on same load
        if (!_.isEmpty(this._OnIHaveFinishedProductsLoaded)) {
            return this._OnIHaveFinishedProductsLoaded
        }

        self._OnIHaveFinishedProductsLoaded = new Promise<IHaveFinishedProducts>((resolve, reject) => {
            // check if BO is loaded
            if (self.bIsLoaded) {
                resolve(self)
                return;
            }

            if (_.isEmpty(self.OnLoadComplete)) {
                reject("Plan failed to Load")
                return;
            }

            self.OnLoadComplete
                .then((aPlanBO) => {

                    self._bIHaveFinishedProductsLoaded = true
                    self._OnIHaveFinishedProductsLoaded = undefined // should we do this, or just hold onto it ?
                    resolve(self)
                })
                .catch((err) => {
                    reject(err)
                })

        })

        return self._OnIHaveFinishedProductsLoaded
    }


    addFinishedProduct(WB_Prod_Id: number): Promise<void> {
        return ASync.error("Adding Finished Products to Plans is not Implemented");
    }

    removeFinishedProduct(WB_Prod_Id: number): void {
        // Not implemented... should not be called
    }

    hasFinishedProduct(WB_Prod_Id: number): boolean {
        return true
    }

//</editor-fold>

    //<editor-fold desc = "IHaveExecutionBounds operations">

    IHaveExecutionBounds_init(): void {
        this._bIHaveExecutionBoundsLoaded = false
        this._OnIHaveExecutionBoundsLoaded = undefined
        this.ExecutionBoundsChanged = new BehaviorSubject<IHaveExecutionBounds>(this)


        // TODO: when needed for plan..
        //  setup events to listen for changes to..Dates/ PGs/ Source Customers
    }

    _bIHaveExecutionBoundsLoaded: boolean
    _OnIHaveExecutionBoundsLoaded: Promise<IHaveExecutionBounds>


    get bIHaveExecutionBoundsLoaded(): boolean {
        return this._bIHaveExecutionBoundsLoaded
    };

    get OnIHaveExecutionBoundsLoaded(): Promise<IHaveExecutionBounds> {

        const self = this

        if (this.bIHaveExecutionBoundsLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(this._OnIHaveExecutionBoundsLoaded)) {
            return this._OnIHaveExecutionBoundsLoaded
        }

        this._OnIHaveExecutionBoundsLoaded = new Promise<IHaveExecutionBounds>((resolve, error) => {
            var childTasks: Promise<any>[] = []
            if (!this.bIHaveExecutionDatesLoaded) {
                childTasks.push(this.OnIHaveExecutionDatesLoaded)
            }

            if (!this.bIHaveSourceCustomersLoaded) {
                childTasks.push(this.OnIHaveSourceCustomersLoaded)
            }

            if (!this.bIHaveProductGroupsLoaded) {
                childTasks.push(this.OnIHaveProductGroupsLoaded)
            }

            if (!this.bIHaveFinishedProductsLoaded) {
                childTasks.push(this.OnIHaveFinishedProductsLoaded)
            }

            if (childTasks.length == 0) {
                this._bIHaveExecutionBoundsLoaded = true
                this._OnIHaveExecutionBoundsLoaded = undefined
                resolve(self)
            } else {
                Promise.all(childTasks)
                    .then((ignored) => {
                        this._bIHaveExecutionBoundsLoaded = true
                        this._OnIHaveExecutionBoundsLoaded = undefined
                        resolve(self)
                    })
                    .catch((err) => {
                        this._OnIHaveExecutionBoundsLoaded = undefined
                        error(err)
                    })
            }

        })

        return this._OnIHaveExecutionBoundsLoaded
    }

    get Execution_Bounds_Identity(): IIdentityProvider {
        return this.Identity
    }

    get Execution_InStore_Aligned(): IDateRange {
        return this.drWB_Plan.InStore.Range
    }

    get Execution_InStore(): IDateRange {
        return this.drWB_Plan.InStore.Range
    }

    get Execution_Shipment(): IDateRange {
        return this.drWB_Plan.Shipment.Range
    }

    get Execution_Shipment_Aligned(): IDateRange {
        return this.drWB_Plan.Shipment.Range
    }

    get Execution_ProductGroups(): WB_ProductGroupRow[] {
        return this.ProductGroups
    }

    get Execution_FinishedProducts(): WB_FinishedProductRow[] {
        return this.FinishedProducts
    }

    get Execution_PlanningCustomers(): WB_CustomerRow[] {
        return [this.drWB_Plan.WB_CustomerRow_Planning]
    }

    get Execution_SourceCustomers(): WB_CustomerRow[] {
        if (!this.SourceCustomers.length) {
            return this.AllSourceCustomers
        }
        return this.SourceCustomers
    }

    ExecutionBoundsChanged: BehaviorSubject<IHaveExecutionBounds>;

    getProductsByProductGroup(wbPGId: number): WB_FinishedProductRow[] {
        let self = this
        let returnVal: WB_FinishedProductRow[] = []
        let pgRow = _.first(_.filter(self.WB_Plan.WB_Plan_ProductGroupRows, aRow => aRow.isActive && aRow.cif.WB_PRDGRP_ID == wbPGId))
        if (!_.isEmpty(pgRow)) {
            returnVal = _.map(pgRow.WB_PlanProductRows, aRow => {
                if (aRow.isActive) return aRow.WB_FinishedProductRow
            })
        }
        return returnVal
    }

//</editor-fold>

    //<editor-fold desc = "IHaveAutoAnchorManager operations">

    private _autoAnchorManager: IAutoAnchorManager

    private _IHaveAutoAnchorManager_init() {
        this._autoAnchorManager = undefined
    }

    private _AutoAnchor_OnBoLoadComplete(): Promise<void> {
        const self = this;
        return new Promise<void>((resolve, error) => {
            self._autoAnchorManager = new AutoAnchorManager(self.PlanEvents)
            resolve()
        })
    }


    get AutoAnchorManager(): IAutoAnchorManager {
        return this._autoAnchorManager
    }

    //</editor-fold>


    //<editor-fold desc = "IHaveSecurity operations">

    private _PolicyCollection: IPolicyCollection;
    private _bIHaveSecurityLoaded: boolean;
    private _OnIHaveSecurityLoaded: Promise<IHaveSecurity>
    private _Lock: ILock

    IHaveSecurity_init() {
        this._PolicyCollection = new PolicyCollection()
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
        this._Lock = undefined
    }

    IHaveSecurity_beforeReload() {
        this._bIHaveSecurityLoaded = false
        this._OnIHaveSecurityLoaded = undefined
    }

    IHaveSecurity_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.OnIHaveSecurityLoaded
                .then((ignore) => {
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    get OnIHaveSecurityLoaded(): Promise<IHaveSecurity> {
        const self = this

        if (this.bIHaveSecurityLoaded) {
            return ASync.resolve(this)
        }

        if (this._OnIHaveSecurityLoaded) {
            return this._OnIHaveSecurityLoaded
        }


        let aHasRolePolicy: PolicyHasRole

        let returnValue = this._OnIHaveSecurityLoaded = (async (): Promise<IHaveSecurity> => {
            let aLock: ILock = undefined

            try {
                if (!PAEnvironment.isNode()) {
                    if (_.isEmpty(self._Lock)) {
                        let aLockService = new PALockClient()
                        aLock = aLockService.createLock("Plan", this.drWB_Plan.cif.WB_PLAN_ID, {
                            object: {
                                UI_Id: self.drWB_Plan.cif.PLAN_ID,
                                Name: self.drWB_Plan.cif.PLAN_NAME
                            }
                        })
                    } else
                        aLock = self._Lock
                    await aLock.OnLoaded()
                    await aLock.acquire()
                    self._Lock = aLock
                }
                let aBOis = new PolicyBOis(['Super_User', 'Event_Analyzer'], [], self.WB_Plan.cif.WB_CUST_ID_PLANNING, aLock)
                self.PolicyCollection.addPolicy("BOis", aBOis)
                aHasRolePolicy = new PolicyHasRole(self.WB_Plan.cif.WB_CUST_ID_PLANNING)
                await aHasRolePolicy.OnLoadComplete
                self.PolicyCollection.addPolicy("HasRole", aHasRolePolicy)
                this._bIHaveSecurityLoaded = true
                return self
            } catch (err) {
                console.log("Unable to setup Security Policies")
                throw err
            }
        })();

        return returnValue

        return this._OnIHaveSecurityLoaded
    }

    get PolicyCollection(): IPolicyCollection {
        return this._PolicyCollection
    }

    get bIHaveSecurityLoaded(): boolean {
        return this._bIHaveSecurityLoaded
    }


//</editor-fold>

    //<editor-fold desc = "IHaveEditorLock
    get EditorLock(): ILock {
        return this._Lock
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveRefCounter
    private _refCounter: IRefCounter

    RefCounter_Init(): void {
        this._refCounter = new RefCounter(this)
        this.RefCounter.OnRefCounterChanged.subscribe(this.onRefCounterChanged.bind(this))
    }

    get RefCounter(): IRefCounter {
        return this._refCounter
    }

    private async onRefCounterChanged(aRefCounter: IRefCounter): Promise<void> {
        if (this.RefCounter.RefCount > 0)
            return

        if (_.isEmpty(this.EditorLock))
            return

        if (this.EditorLock.Status === "Locked") {
            let events = this.PlanEvents.Events
            for (let i = 0; i < events.length; i++) {
                let anEvent = events[i]
                if (!_.isEmpty(anEvent) && anEvent.RefCounter.RefCount > 0)
                    await anEvent.RefCounter.releaseReference()
            }
            await this.EditorLock.release()

        }

    }

    //</editor-fold>


    //<editor-fold desc = "IRecordResolverProvider   <<STATIC>> operations">


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts)
            dbContext = opts.dbContext;
        else {
            dbContext = new PAdbContext({});
        }
        const pln_options: CreateOptions = {
            standardConditions: {
                [esWB_Plan]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Plan_Product_Group]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Plan_Product]: {"DELETED_IND": {"notIn": ['1']}}
            },
            dctIncludeBelongsToNamedRelations: {},
            dctExcludeHasManyNamedRelations: {
                [esWB_Plan]: [esWB_Plan_Volume_Metrics, esWB_Plan_Redistrib_Volume_Metrics, esWB_Plan_Volume_Monthly_Metrics, esWB_Plan_Funding_Metrics]
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, pln_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Plan, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })

    }

    static concurrentLoadCountHint(): number {
        return 5;
    }

//</editor-fold>

};
PAEnvironment.registerBusinessObjectClass(PlanBO, "PlanBO", "Plan")
